import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  SplashScreen,
  View,
  Stack,
  Button,
  Spinner,
  Tooltip,
  Text,
  StyledContainer,
  withTranslation,
} from '@piwikpro/ui-components';
import {
  ConfigService,
  CopyIcon,
  errorBoundary,
  withInject,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { useParams } from '@tanstack/react-router';
import { Users } from '@piwikpro/users-crate';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { AdministrationState } from '../../../redux';
import { useUsersFetchParams, UsersFetchParams } from '../useUsersFetchParams';
import LanguageAndRegion from './Profile/LanguageAndRegion';
import Security from './Profile/Security';
import Profile from './Profile';
import styles from './UserDetails.module.css';

const mapStateToProps = (state: AdministrationState & { session: SessionState }) => {
  const { details } = state.users.details.user;

  return {
    loggedUserId: state.session.userId,
    user: details as NonNullable<typeof details>,
    isLoading: state.users.details.user.isLoading,
    failed: state.users.details.user.failed,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  users: Users
  Title: any
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
  fetchUsersParams: UsersFetchParams
  userId: string
}

class UserDetails extends React.Component<Props> {
  constructor(props) {
    super(props);

    this.handleUserDeletion = this.handleUserDeletion.bind(this);
  }

  async componentDidMount() {
    const { users, userId } = this.props;

    if (userId) {
      try {
        await users.getUserDetails(userId);
      // eslint-disable-next-line no-empty
      } catch (err) {}
    }
  }

  async componentDidUpdate(prevProps) {
    const { users, userId } = this.props;

    if (userId && userId !== prevProps.userId) {
      try {
        await users.getUserDetails(userId);
      // eslint-disable-next-line no-empty
      } catch (err) {}
    }
  }

  async handleUserDeletion() {
    const {
      user,
      users,
      fetchUsersParams,
      routerService,
    } = this.props;

    try {
      await users.deleteUser(user.id, user.attributes.email);
      routerService.push({ to: '/administration/users' });
      users.fetchUsers(fetchUsersParams);
    // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  renderDeleteButton() {
    const {
      user,
      loggedUserId,
      t,
    } = this.props;

    const isLoggedUser = user.id === loggedUserId;

    const deleteButton = disabled => (
      <Button
        appearance="default"
        text={t('users.actions.deleteUser')}
        onClick={this.handleUserDeletion}
        disabled={disabled}
      />
    );

    if (isLoggedUser) {
      return (
        <Tooltip content={t('users.actions.forbidden.deleteLoggedInUser.info')}>
          {deleteButton(true)}
        </Tooltip>
      );
    }

    return deleteButton(false);
  }

  render() {
    const {
      user,
      isLoading,
      t,
      failed,
      Title,
    } = this.props;

    if (isLoading) {
      return (
        <Spinner type="centered" className="x-large" />
      );
    }

    if (failed) {
      return (
        <SplashScreen type="notFound" />
      );
    }

    const header = (
      <StyledContainer border={['bottom']} padding="mid" backgroundColor="gray">
        <View.Header
          title={user.attributes.email || ''}
          caption={(
            <Stack vertical spacing="narrow">
              <Stack.Item previousSpacing="narrow">
                <div className={styles.idRow}>
                  <Text as="p" size={12} color="InkLighter" dataId="ViewHeader.caption">
                    {t('users.details.subTitle', { id: user.id })}
                  </Text>
                  <CopyIcon value={user.id} />
                </div>
              </Stack.Item>
              <Stack.Item>
                <LanguageAndRegion />
              </Stack.Item>
            </Stack>
          )}
        >
          <Security />
          {this.renderDeleteButton()}
        </View.Header>
      </StyledContainer>
    );

    return (
      <Title
        part={user.attributes.email}
        important
      >
        <View fullHeight overflowTopElement={header}>
          <StyledContainer padding="mid" withoutTopPadding>
            <View.Body>
              <Profile />
            </View.Body>
          </StyledContainer>
        </View>
      </Title>
    );
  }
}

const UserDetailsConnected = connector(withInject<InjectedProps>({
  users: 'UsersCrate.users',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  withTranslation(['administration', 'platform'])(errorBoundary(UserDetails)),
));

interface UserDetailsWrapperProps {
  config: ConfigService
  userPreferences: any
}

const UserDetailsWrapper: React.FC<UserDetailsWrapperProps> = ({ config, userPreferences, ...props }) => {
  const params = useParams({ from: '/_secured/administration/users/$userId' });
  const fetchUsersParams = useUsersFetchParams(config, userPreferences);

  return (
    <UserDetailsConnected
      {...props}
      userId={params.userId}
      fetchUsersParams={fetchUsersParams}
    />
  );
};

export default withInject<UserDetailsWrapperProps>({
  config: 'config',
  userPreferences: 'userPreferences',
})(UserDetailsWrapper);
